import ContentPager, { Page } from '@liveauctioneers/caterwaul-components/lib/ContentPager/ContentPager';
import Modal from '@liveauctioneers/caterwaul-components/lib/Modal/Modal';
import ModalHeader from '@liveauctioneers/caterwaul-components/lib/Modal/ModalHeader';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import React, { useEffect, useState } from 'react';

import { modalTypes } from '@/enums';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { dismissModal } from '@/redux/modules/modal';
import { getOpenModal } from '@/redux/modules/modal.selectors';
import {
    getUiSendSellerMessage,
    getUiSendSellerMessageSuccess,
    startConversationWithAttachment,
} from '@/redux/modules/sendSellerMessage';

import SendSellerConsignmentMessageConfirm from './SendSellerConsignmentMessageConfirm';
import SendSellerConsignmentMessageModalDetails from './SendSellerConsignmentMessageModalDetails';
import SendSellerConsignmentMessageReasons from './SendSellerConsignmentMessageReasons';
import { SendSellerConsignmentMessagePageName } from './types/SendSellerConsignmentMessagePageName';

const SendSellerConsignmentMessageModal = () => {
    const [currentPage, setCurrentPage] = useState<SendSellerConsignmentMessagePageName>('send-consignment-message');

    const dispatch = useAppDispatch();
    const {
        error = false,
        messageDetails = {},
        sellerId = 0,
        sellerName = '',
        submitted = false,
    } = useAppSelector(getUiSendSellerMessage);

    const sendSellerMessageSuccess = useAppSelector(getUiSendSellerMessageSuccess);

    const show = useAppSelector(getOpenModal) === modalTypes.SEND_SELLER_CONSIGNMENT_MESSAGE;

    useEffect(() => {
        if (sendSellerMessageSuccess && currentPage === 'send-consignment-message') {
            setCurrentPage('send-consignment-message-success');
        }
    }, [setCurrentPage, currentPage, sendSellerMessageSuccess]);

    const closeModal = () => {
        dispatch(dismissModal());
    };

    const submitConsignment = React.useCallback((args) => dispatch(startConversationWithAttachment(args)), [dispatch]);
    const isMobile = useIsNarrowerThan(450);

    const modalBody = (
        <ContentPager currentPage={currentPage}>
            <Page
                name="send-consignment-message"
                testid="send-consignment-message"
            >
                <SendSellerConsignmentMessageModalDetails
                    isMobile={isMobile}
                    messageDetails={messageDetails}
                    sellerId={sellerId}
                    subject="Consignment Inquiry"
                    submitConsignment={submitConsignment}
                    uiError={error}
                    uiSubmitted={submitted}
                />
            </Page>
            <Page
                name="send-consignment-message-success"
                testid="send-consignment-message-success"
            >
                <SendSellerConsignmentMessageConfirm
                    dismissModal={closeModal}
                    sellerName={sellerName}
                    uiError={error}
                />
            </Page>
        </ContentPager>
    );

    const header = (
        <ModalHeader
            onClose={closeModal}
            title={
                currentPage === 'send-consignment-message' ? (
                    <FormattedMessage
                        id="consign_with"
                        values={{ sellerName }}
                    />
                ) : (
                    <FormattedMessage id="thank_you" />
                )
            }
        />
    );

    const footer = !isMobile && <SendSellerConsignmentMessageReasons sellerName={sellerName} />;

    return (
        <Modal
            body={modalBody}
            contentLabel="SubmitConsignmentMessageModal"
            footer={currentPage === 'send-consignment-message' && footer}
            header={header}
            show={show}
        />
    );
};

export default SendSellerConsignmentMessageModal;
